import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Diff } from "@/features/FormCard/Diff";
import { parseFloatValue, parseToDateValue } from "@/features/FormCard/utils";

import { DatePickerField, InputField } from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { dateSchema } from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";

const table_name = "a7_opcvm_compositions";

const FormSchema = z.object({
  date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
  a7_opcvm_strategy_id: z
    .preprocess((val) => parseInt(val as string, 10), z.number().int())
    .nullable(),
  a7_opcvm_underlying_id: z
    .preprocess((val) => parseInt(val as string, 10), z.number().int())
    .nullable(),
  a7_opcvm_time_serie_file_id: z
    .preprocess((val) => parseInt(val as string, 10), z.number().int())
    .nullable(),
  valorisation_part_du_sous_jacent: z
    .preprocess((val) => parseFloatValue(val as string), z.number().nullable())
    .nullable(),
  detention_du_fonds_dans_le_sous_jacent: z
    .preprocess((val) => parseFloatValue(val as string), z.number().nullable())
    .nullable(),
  version: z
    .preprocess((val) => parseInt(val as string, 10), z.number().int())
    .nullable(),
});

export function A7OPCVMCompositionsEditCard({ id }: { id: number }) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    id,
    table_name,
    FormSchema,
    client: "a7",
    disable_translations_request: true,
  });

  return (
    <div className="grid place-items-center bg-gray-50 h-full">
      <div className="flex justify-center">
        <Card className="p-4 bg-white">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Compositions",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-2">
                <div className=" grid grid-cols-1 md:grid-cols-2 gap-2">
                  <DatePickerField
                    name="date"
                    label={t(`${table_name}.columns.date`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    isRequired={true}
                    control={form.control}
                  />
                  <InputField
                    name="version"
                    label={t(`${table_name}.columns.version`, {
                      ns: "tables",
                      defaultValue: "Version",
                    })}
                    isRequired={true}
                    control={form.control}
                    type="number"
                    allowNegative={false}
                  />
                </div>
                <div className=" grid grid-cols-1 md:grid-cols-2 gap-2">
                  <InputField
                    name="a7_opcvm_strategy_id"
                    label={t(`${table_name}.columns.a7_opcvm_strategy_id`, {
                      ns: "tables",
                      defaultValue: "Fonds",
                    })}
                    isRequired={true}
                    control={form.control}
                  />
                  <InputField
                    name="a7_opcvm_underlying_id"
                    label={t(`${table_name}.columns.a7_opcvm_underlying_id`, {
                      ns: "tables",
                      defaultValue: "Instrument financier",
                    })}
                    isRequired={true}
                    control={form.control}
                  />
                </div>
                <div className=" grid grid-cols-1 md:grid-cols-2 gap-2">
                  <InputField
                    name="valorisation_part_du_sous_jacent"
                    label={t(
                      `${table_name}.columns.valorisation_part_du_sous_jacent`,
                      { ns: "tables", defaultValue: "Valorisation" },
                    )}
                    control={form.control}
                  />
                  <InputField
                    name="detention_du_fonds_dans_le_sous_jacent"
                    label={t(
                      `${table_name}.columns.detention_du_fonds_dans_le_sous_jacent`,
                      { ns: "tables", defaultValue: "Détention" },
                    )}
                    control={form.control}
                  />
                </div>
                <InputField
                  name="a7_opcvm_time_serie_file_id"
                  label={t(
                    `${table_name}.columns.a7_opcvm_time_serie_file_id`,
                    { ns: "tables", defaultValue: "Fichier" },
                  )}
                  isRequired={true}
                  control={form.control}
                />
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  client="a7"
                  elementId={id}
                  isDirty={form.formState.isDirty}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
          children={changedFields ? Diff(changedFields) : t("no_changes")}
        />

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={closeDeleteModal}
        />
      </div>
    </div>
  );
}
